import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import type { Persister, PersistedClient } from '@tanstack/react-query-persist-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider } from 'react-redux';

import './index.css';
import App from './App';
import { Auth0ProviderWithNavigate, AxiosInterceptor } from './pages/auth';
import reportWebVitals from './reportWebVitals';
import { store } from './store';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const CACHE_KEY = 'cms2-query-cache';

const localStoragePersister: Persister = {
  persistClient: (client: PersistedClient) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(client));
    } catch {
      // localStorage full or unavailable — silently discard
    }
  },
  restoreClient: () => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      return raw ? (JSON.parse(raw) as PersistedClient) : undefined;
    } catch {
      return undefined;
    }
  },
  removeClient: () => {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {
      // ignore
    }
  },
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      gcTime: 30 * 60 * 1000,
    },
  },
});

root.render(
  <Router>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: localStoragePersister, maxAge: 30 * 60 * 1000 }}
    >
      <Auth0ProviderWithNavigate>
        <AxiosInterceptor>
          <Provider store={store}>
            <App />
          </Provider>
        </AxiosInterceptor>
      </Auth0ProviderWithNavigate>
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  </Router>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
